import { computed, inject } from '@angular/core';
import { InstallationRefusal, toRefusal } from '@core/helpers/installation-refusal.helper';
import { Ambiguity, DataSources, MeasurementPoint } from '@core/models/installation.model';
import { InstallationDataSourcesService } from '@core/services/installation-data-sources.service';
import { tapResponse } from '@ngrx/operators';
import { patchState, signalStore, withComputed, withMethods, withState } from '@ngrx/signals';
import { rxMethod } from '@ngrx/signals/rxjs-interop';
import { pipe, switchMap, tap } from 'rxjs';

type InstallationDataSourcesState = {
  installationOid: string | null;
  isLoading: boolean;
  dataSources: DataSources | null;
  loadError: InstallationRefusal | null;
  showHidden: boolean;
  isLoadingAllPoints: boolean;
  // Hidden points included; read only once the toggle is on.
  allPoints: MeasurementPoint[] | null;
  allPointsFailed: boolean;
};

const initialState: InstallationDataSourcesState = {
  installationOid: null,
  isLoading: false,
  dataSources: null,
  loadError: null,
  showHidden: false,
  isLoadingAllPoints: false,
  allPoints: null,
  allPointsFailed: false,
};

// Provided by each component that needs it (the tabs, the device select), so it holds one
// installation and reads only while that component is shown. The dialogs never see it: a dialog
// closes with its result and the tab reloads.
export const InstallationDataSourcesStore = signalStore(
  withState(initialState),
  withComputed(store => ({
    points: computed<MeasurementPoint[]>(() => {
      const allPoints = store.allPoints();
      return store.showHidden() && allPoints !== null
        ? allPoints
        : (store.dataSources()?.points ?? []);
    }),
    ambiguities: computed<ReadonlyMap<string, Ambiguity>>(
      () =>
        new Map(
          (store.dataSources()?.ambiguities ?? []).map(entry => [entry.pointOid, entry.ambiguity]),
        ),
    ),
  })),
  withMethods((store, service = inject(InstallationDataSourcesService)) => {
    const readDataSources = rxMethod<string>(
      pipe(
        tap(() => patchState(store, { isLoading: true, loadError: null })),
        switchMap(installationOid =>
          service.dataSources(installationOid).pipe(
            tapResponse({
              next: dataSources => patchState(store, { dataSources, isLoading: false }),
              error: (error: unknown) =>
                patchState(store, {
                  dataSources: null,
                  isLoading: false,
                  loadError: toRefusal(error),
                }),
            }),
          ),
        ),
      ),
    );

    const readAllPoints = rxMethod<string>(
      pipe(
        tap(() => patchState(store, { isLoadingAllPoints: true, allPointsFailed: false })),
        switchMap(installationOid =>
          service.allPoints(installationOid).pipe(
            tapResponse({
              next: allPoints => patchState(store, { allPoints, isLoadingAllPoints: false }),
              error: () =>
                patchState(store, {
                  allPoints: null,
                  isLoadingAllPoints: false,
                  allPointsFailed: true,
                }),
            }),
          ),
        ),
      ),
    );

    return {
      load(installationOid: string): void {
        patchState(store, { ...initialState, installationOid });
        readDataSources(installationOid);
      },

      // After a write, so the hidden list moves with the rest when it is shown.
      reload(): void {
        const installationOid = store.installationOid();
        if (installationOid === null) return;
        readDataSources(installationOid);
        if (store.showHidden()) {
          readAllPoints(installationOid);
        } else {
          patchState(store, { allPoints: null });
        }
      },

      setShowHidden(showHidden: boolean): void {
        patchState(store, { showHidden });
        const installationOid = store.installationOid();
        if (showHidden && installationOid !== null && store.allPoints() === null) {
          readAllPoints(installationOid);
        }
      },
    };
  }),
);
